import { AssetGroup } from '@play-co/timestep-core';
import ImageScaleView from '@play-co/timestep-core/lib/ui/ImageScaleView';
import View from '@play-co/timestep-core/lib/ui/View';
import { ImageSliceView } from '@play-co/timestep-core/ui';
import PopupBasic from 'src/game/components/popups/PopupBasic';
import { requestAndClaimIncentivizedOAAddition } from 'src/game/chatbot/chatbot.api';
import i18n from 'src/lib/i18n/i18n';
import sounds from 'src/lib/sounds';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { type IncentivizedOARewardClaimResult } from 'src/replicant/chatbot/chatbot.ruleset';

/** Result returned when a persistent OA incentive popup closes. */
export type IncentivizedOAReminderResult =
  | IncentivizedOARewardClaimResult
  | 'close_button';

/** Callback used to close a persistent OA incentive popup. */
type CloseHandler = (result: IncentivizedOAReminderResult) => void;

/**
 * Persistent OA incentive popup for FTUE completion and milestone wins.
 */
export default class PopupIncentivizedOAReminder extends PopupBasic<CloseHandler> {
  private readonly close: CloseHandler;
  /** Offered amount kept locally until this popup is accepted or dismissed. */
  private amount = 0;
  /** Reward label refreshed for each popup opening. */
  private readonly amountLabel: LangBitmapFontTextView;

  /**
   * Compose the reminder from the supplied character art and existing UI assets.
   *
   * @param opts Popup parent and typed close callback.
   */
  public constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      ...opts,
      close: () => opts.close('close_button'),
      zIndex: 10000,
      width: 558,
      height: 720,
      tracking: {
        feature: 'resubscribe',
        $subFeature: 'resubscribe-incentivized-oa-reminder',
      },
    });

    this.close = opts.close;

    // Use the supplied Level 15 character art as the visual focus.
    new ImageScaleView({
      superview: this.box,
      image: 'assets/ui/incentivizedOA/image.png',
      x: this.box.style.width / 2,
      y: 270,
      width: 470,
      height: 236,
      centerOnOrigin: true,
      centerAnchor: true,
      scaleMethod: 'contain',
    });

    const rewardPanel = new ImageSliceView({
      superview: this.box,
      x: this.box.style.width / 2,
      y: 440,
      width: 220,
      height: 94,
      image: 'assets/ui/shared/frames/inset_panel.png',
      slices: {
        left: 25,
        right: 25,
        top: 25,
        bot: 25,
      },
      centerOnOrigin: true,
      centerAnchor: true,
    });

    // Display the reward amount in the Body font.
    this.amountLabel = new LangBitmapFontTextView({
      superview: rewardPanel,
      x: 26,
      y: 10,
      width: 128,
      height: 72,
      align: 'center',
      verticalAlign: 'center',
      size: 50,
      color: '#FFFFFF',
      font: 'Body',
      canHandleEvents: false,
      localeText: () => this.amount.toString(),
    });

    // Pair the amount with the existing gem language used throughout the game.
    new ImageScaleView({
      superview: rewardPanel,
      image: 'assets/ui/hud/icons/hud_gem.png',
      x: 154,
      y: 18,
      width: 58,
      height: 58,
      scaleMethod: 'contain',
    });

    // Place the explanatory copy directly above the CTA.
    this.message.updateOpts({
      x: 35,
      y: 492,
      width: this.box.style.width - 70,
      height: 78,
      size: 30,
      color: '#A9724B',
      font: 'Body',
      wordWrap: true,
    });

    const addFriendButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      localeText: () => i18n('incentivizedOA.reminder.cta'),
      onClick: async () => {
        // Prevent repeated taps while LINE's half-modal is open.
        addFriendButton.setDisabled(true);
        sounds.sfx.play('ok');

        // Keep this popup open while LINE requests and verifies friendship.
        const result = await requestAndClaimIncentivizedOAAddition();
        if (result === 'not_subscribed') {
          addFriendButton.setDisabled(false);
          return;
        }

        // Close only after a terminal claim result; the sequence handles reward UI.
        this.close(result);
      },
      icon: {
        image: 'assets/ui/resubscribe/add_friend_icon.png',
        width: 57,
        height: 52,
        offset: {
          x: -165,
          y: 0,
        },
      },
      font: 'Body',
      fontSize: 42,
      width: 430,
      height: 90,
      x: this.box.style.width / 2,
      y: 630,
      labelOffsetY: -4,
      labelPaddingX: 0,
      labelPaddingY: 0,
      centerOnOrigin: true,
      tracking: {
        feature: 'resubscribe-incentivized-oa-reminder',
        $subFeature: 'resubscribe-incentivized-oa-reminder-add-friend',
      },
    });
  }

  /**
   * Return all supplied and existing assets used by the reminder.
   *
   * @returns Asset group for the reminder composition.
   */
  protected getAssetsGroup(): AssetGroup {
    return new AssetGroup([
      'assets/ui/incentivizedOA/image.png',
      'assets/ui/shared/frames/inset_panel.png',
      'assets/ui/hud/icons/hud_gem.png',
      'assets/ui/resubscribe/add_friend_icon.png',
    ]);
  }

  /**
   * Apply localized Body-font copy and display the reminder.
   *
   * @param args Amount captured when the offer was shown.
   */
  public override init(args: { amount: number }): void {
    // Keep the CTA and every label tied to the same offered amount.
    this.amount = args.amount;
    this.amountLabel.notifyLocaleTextChanged();

    // Use the standard title banner, whose configured typeface is Body.
    super.init({
      title: i18n('incentivizedOA.reminder.title'),
      message: i18n('incentivizedOA.reminder.message', {
        value: this.amount,
      }),
    });

    // It is a long multiline title, adjust style to fit the box nicely
    this.title.title.updateOpts({
      size: 28,
      leading: -20,
      offsetY: 3,
    });
  }
}
